import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtKg, fmtNum, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageLoading } from "@/components/layout/Layout";
import { Drop } from "@phosphor-icons/react";
import { useT } from "@/contexts/I18nContext";
import { SystemBadge } from "@/components/Badges";
import { ExportMenu } from "@/components/ExportMenu";

function fmtProd(r) {
    if (r.production_unit === "L") return `${fmtNum(r.production_amount, 2)} L`;
    return fmtKg(r.production_amount);
}

const ALL = "all";

export default function Reports() {
    const { t } = useT();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [system, setSystem] = useState(ALL);
    const [ptype, setPtype] = useState(ALL);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    useEffect(() => {
        api.get("/reports")
            .then((r) => setItems(r.data))
            .catch(() => { })
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => items.filter((r) => {
        const s = q.toLowerCase();
        if (s && !(r.rfid.toLowerCase().includes(s) || r.animal_name.toLowerCase().includes(s) || r.lot.toLowerCase().includes(s))) return false;
        if (system !== ALL && r.production_system !== system) return false;
        if (ptype !== ALL && r.production_type !== ptype) return false;
        if (dateFrom && r.period_end < dateFrom) return false;
        if (dateTo && r.period_end > `${dateTo}T23:59:59`) return false;
        return true;
    }), [items, q, system, ptype, dateFrom, dateTo]);

    const exportFilters = useMemo(() => ({
        production_system: system !== ALL ? system : undefined,
        production_type: ptype !== ALL ? ptype : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo ? `${dateTo}T23:59:59` : undefined,
    }), [system, ptype, dateFrom, dateTo]);

    return (
        <div className="space-y-6" data-testid="reports-page">
            <header className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{t("reports_title")}</h1>
                    <p className="text-sm text-muted-foreground mt-1">{t("reports_subtitle")}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Input placeholder={t("search_reports")} value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="reports-search" />
                    <ExportMenu endpoint="/export/reports" filters={exportFilters} baseName="fcr_reports" testIdPrefix="reports-export" />
                </div>
            </header>

            <Card className="card-paper shadow-none p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                        <Label className="text-xs">{t("production_system")}</Label>
                        <Select value={system} onValueChange={setSystem}>
                            <SelectTrigger data-testid="reports-filter-system"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>—</SelectItem>
                                <SelectItem value="feedlot">{t("sys_feedlot")}</SelectItem>
                                <SelectItem value="semi">{t("sys_semi")}</SelectItem>
                                <SelectItem value="pasture">{t("sys_pasture")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">{t("production_type_label")}</Label>
                        <Select value={ptype} onValueChange={setPtype}>
                            <SelectTrigger data-testid="reports-filter-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>—</SelectItem>
                                <SelectItem value="weight">{t("production_weight")}</SelectItem>
                                <SelectItem value="milk">{t("production_milk")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">{t("date_from")}</Label>
                        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="reports-filter-from" />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">{t("date_to")}</Label>
                        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="reports-filter-to" />
                    </div>
                </div>
            </Card>

            {loading ? <PageLoading /> : (
                <Card className="card-paper shadow-none overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t("date")}</TableHead>
                                <TableHead>{t("animal")}</TableHead>
                                <TableHead>{t("production_system")}</TableHead>
                                <TableHead className="text-right">{t("production_label")}</TableHead>
                                <TableHead className="text-right">{t("total_feed_period")}</TableHead>
                                <TableHead className="text-right">{t("animal_weighing")}</TableHead>
                                <TableHead className="text-right">{t("conversion_rate")}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.length === 0 ? (
                                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">{t("no_reports")}</TableCell></TableRow>
                            ) : filtered.map((r) => (
                                <TableRow key={r.id} className="hover:bg-[#EAF3DA]/40">
                                    <TableCell>
                                        <div>{fmtDate(r.period_end)}</div>
                                        {r.duration_days != null ? (
                                            <div className="text-[10px] text-muted-foreground">{r.duration_days} {t("days")}</div>
                                        ) : null}
                                    </TableCell>
                                    <TableCell>
                                        <Link to={`/animales/${r.animal_id}`} className="font-medium text-[#083D1E] hover:underline">{r.animal_name}</Link>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                            <span className="text-xs text-muted-foreground">{r.rfid}</span>
                                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{r.lot}</Badge>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <SystemBadge system={r.production_system || "feedlot"} />
                                        {r.diet_type ? (
                                            <div className="text-[10px] text-muted-foreground mt-1 max-w-[160px] truncate" title={r.diet_type}>
                                                {r.diet_type}
                                            </div>
                                        ) : null}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {r.production_type === "milk" ? (
                                            <div className="flex items-center justify-end gap-1.5">
                                                <Drop size={13} color="#4A6E00" weight="fill" />
                                                <span className="metric-number text-[#4A6E00]">{fmtProd(r)}</span>
                                            </div>
                                        ) : (
                                            <span className="metric-number text-[#083D1E]">{fmtProd(r)}</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="metric-number">{fmtKg(r.total_feed_as_fed)}</div>
                                        <div className="text-[10px] text-muted-foreground">MS: {fmtKg(r.total_feed_dry_matter)}</div>
                                    </TableCell>
                                    <TableCell className="text-right metric-number text-muted-foreground">
                                        {r.production_type === "milk" ? "—" : fmtKg(r.end_weight)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="metric-number text-[#083D1E] font-semibold">{fmtNum(r.fcr_as_fed)}</div>
                                        <div className="text-[10px] text-[#4A6E00] font-medium">MS: {fmtNum(r.fcr_dry_matter)}</div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}
        </div>
    );
}
